import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, unref as _unref, normalizeStyle as _normalizeStyle, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, withCtx as _withCtx, resolveComponent as _resolveComponent, createTextVNode as _createTextVNode, withModifiers as _withModifiers, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = ["data-contact-id"]
const _hoisted_2 = { class: "client-item__header" }
const _hoisted_3 = { class: "client-item__header-main" }
const _hoisted_4 = ["innerHTML"]
const _hoisted_5 = { class: "client-item__header-address" }
const _hoisted_6 = { class: "client-item__subheader" }
const _hoisted_7 = ["innerHTML", "onClick"]
const _hoisted_8 = ["innerHTML"]
const _hoisted_9 = { class: "client-item__organizations" }
const _hoisted_10 = {
  key: 1,
  class: "client-item__organizations-add-clients"
}
const _hoisted_11 = { class: "client-item__organizations-list-item" }
const _hoisted_12 = ["innerHTML"]
const _hoisted_13 = { class: "client-item__clients" }
const _hoisted_14 = { class: "client-item__clients-item-phone-field" }
const _hoisted_15 = ["innerHTML", "onClick"]
const _hoisted_16 = { class: "client-item__clients-item-email-field" }
const _hoisted_17 = ["innerHTML", "onClick"]
const _hoisted_18 = ["innerHTML", "onClick"]
const _hoisted_19 = ["innerHTML"]
const _hoisted_20 = { class: "client-item__footer" }
const _hoisted_21 = { class: "client-item__footer-tags" }
const _hoisted_22 = ["innerHTML"]
const _hoisted_23 = { class: "client-item__footer-options" }
const _hoisted_24 = { class: "client-item__actions-menu" }

import {
	computed,
	nextTick,
	onBeforeUnmount,
	ref
} from "vue";
import {
	Client,
	clientsStore
} from "@/store/clients";
import ContactInfoModal from "@/components/main/clients-panel/modals/ContactInfoModal.vue";
import {splitTextByPhoneNumbers} from "@/utils/contactsHelpers";
import { i18n } from "@/i18n";
import {PersonApi} from "@/ api/PersonApi";
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue";
import AppCheckbox from "@/components/ui-kit/checkbox/AppCheckbox.vue";
import { highlightSearchText } from "@/utils/textHelpers";
import AppLoader from "@/components/common/AppLoader.vue";
import AppResizableDropdown from "@/components/ui-kit/dropdown/AppResizableDropdown.vue";
import ContactAddClientDropdown from "@/components/main/clients-panel/dropdowns/ContactAddClientDropdown.vue";
import CreateEditContactDropdown from "@/components/main/clients-panel/dropdowns/CreateEditContactDropdown.vue";
import ConfirmActionDropdown from "@/components/main/clients-panel/dropdowns/ConfirmActionDropdown.vue";
import AppTagSelector from "@/components/ui-kit/tag-selector/AppTagSelector.vue";
import AppTooltip from "@/components/ui-kit/tooltip/AppTooltip.vue";
import { TagEntityType } from "@/models/etc/types";
import { TagWithFrequency } from "@/models/interfaces/tags/tag-models";
import ContactsEmailPhoneOptionsDropdown
	from "@/components/main/clients-panel/dropdowns/ContactsEmailPhoneOptionsDropdown.vue";
import {buildAbsoluteClientUrl, normalizeUrlForOpening} from "@/utils/urlHelpers";
import {copyTextToClipboard} from "@/utils/clipboard";
import {useBreakpoints} from "@/composables/useBreakpoints";
import {useContactsContext} from "@/composables/useContactsContext";
import {useContactsAccess} from "@/composables/useContactsAccess";

const SHARE_TOOLTIP_KEY = 'ui_share'
const SHARE_COPIED_KEY = 'link_copied_to_clipboard'
const SHARE_COPIED_DELAY = 3000


export default /*@__PURE__*/_defineComponent({
  __name: 'ClientsPanelListItem',
  props: {
    client: {},
    searchText: {}
  },
  setup(__props: any) {

const props = __props

const store = clientsStore()
const ctx = useContactsContext()
const { isContactsReadonly } = useContactsAccess()
const { isMobile, isTablet } = useBreakpoints()

const $addClientToContact = ref<typeof AppResizableDropdown | null>(null)
const $clientInfoModal = ref<InstanceType<typeof ContactInfoModal> | null>(null)
const $createEditContactModal = ref<InstanceType<typeof AppResizableDropdown> | null>(null)
const $deleteContactDropdown = ref<InstanceType<typeof AppResizableDropdown> | null>(null)
const $addTag = ref<typeof AppResizableDropdown | null>(null)
const $shareTooltip = ref<typeof AppTooltip | null>(null)
const $contactActionsDropdown = ref<InstanceType<typeof AppResizableDropdown> | null>(null)

const shareTooltipKey = ref(SHARE_TOOLTIP_KEY)
const shareTooltipText = computed(() => String(i18n.global.t(shareTooltipKey.value)))
const hasAttachedClients = computed(() => !!props.client.Clients?.length)

let shareTooltipTimer: ReturnType<typeof setTimeout> | null = null

const contactTagsModel = computed<TagWithFrequency[]>(
	() => (props.client.Tags ?? []) as unknown as TagWithFrequency[]
)

const onSelectorChange = async (nextTags: TagWithFrequency[]) => {
	if (isContactsReadonly.value) {
		return
	}
	const prevIds = new Set((props.client.Tags ?? []).map(t => t.Id))
	const nextIds = new Set(nextTags.map(t => t.Id))
	const added = nextTags.filter(t => !prevIds.has(t.Id))
	const removedIds = [...prevIds].filter(id => !nextIds.has(id))
	for (const tag of added) {
		await store.attachTagToContact(props.client, tag.Name)
	}
	for (const id of removedIds) {
		await store.detachTagFromContact(props.client, id)
	}
}

const onCreateTag = async (name: string) => {
	if (isContactsReadonly.value) {
		return
	}
	await store.attachTagToContact(props.client, name)
}

const onDetachTag = async (tagId: number) => {
	if (isContactsReadonly.value) {
		return
	}
	await store.detachTagFromContact(props.client, tagId)
}

const clientAddress = computed(() => {
	const addressLvl2 = props.client.AddressChain?.find(lvl => lvl.Level === 2)
	return addressLvl2 ? addressLvl2.Name : '';
})

const contactsArray = computed(() => {
	return props.client.Contacts
		? props.client.Contacts.split('\n')
		: []
})

const highlightedName = computed(() =>
	highlightSearchText(props.searchText, props.client.Name)
)

const postSegments = computed(() =>
	splitTextByPhoneNumbers(props.client.Post, props.searchText).map(el =>
		(el.isPhone || el.isEmail || el.isUrl)
			? el
			: { ...el, text: highlightSearchText(props.searchText, el.text) }
	)
)

const contactsSegments = computed(() =>
	contactsArray.value.map(item => splitTextByPhoneNumbers(item, props.searchText))
)

const loaderStyle = computed(() => ({
	'background-color': 'rgba(255, 255, 255, 0.5)'
}))

const currentEditedClientId = computed(() => store.getEditedClient)
const isEditedClient = computed(() => currentEditedClientId.value === props.client.Id)

const callPhone = async (phoneNumber: string) => {
	const clearNumber = encodeURIComponent(
		phoneNumber.replace(/<span class="searchedText">|<\/span>/g,
		''
		)
	)
	await PersonApi.callPhone(clearNumber)
}

const onConfirmDelete = async () => {
	$deleteContactDropdown.value?.close()
	if (isContactsReadonly.value) {
		return
	}

	if (ctx.refetchContacts) {
		await PersonApi.deleteContact(props.client.Id)
		await ctx.refetchContacts()
	} else {
		store.deleteClient(props.client.Id)
	}
}

const onDeleteClient = async (clientId: number) => {
	if (isContactsReadonly.value) {
		return
	}
	await store.detachClientFromContact(props.client, clientId)
}

const $detachDropdowns = new Map<number, { close: () => void }>()

const setDetachDropdown = (id: number, el: any) => {
	if (el) {
		$detachDropdowns.set(id, el)
	} else {
		$detachDropdowns.delete(id)
	}
}

const closeDetachDropdown = (id: number) => {
	$detachDropdowns.get(id)?.close()
}

const onConfirmDetachClient = async (clientId: number) => {
	closeDetachDropdown(clientId)
	await onDeleteClient(clientId)
}

const onMainInProjectChange = async () => {
	if (ctx.projectId == null) {
		return
	}
	await PersonApi.setMainInProjectStatus(ctx.projectId, props.client.Id)
	if (ctx.refetchContacts) {
		await ctx.refetchContacts()
	}
}

const onCopyClientRoute = async () => {
	const targetClient = props.client.Clients?.[0]
	if (!targetClient) {
		return
	}

	const copied = await copyTextToClipboard(
		buildAbsoluteClientUrl(
			`main?page=client&id=${targetClient.Id}&contactId=${props.client.Id}`
		)
	)
	if (!copied) {
		return
	}

	if (shareTooltipTimer) {
		clearTimeout(shareTooltipTimer)
	}
	shareTooltipKey.value = SHARE_COPIED_KEY
	$shareTooltip.value?.showByClick()
	shareTooltipTimer = setTimeout(() => {
		shareTooltipKey.value = SHARE_TOOLTIP_KEY
		shareTooltipTimer = null
	}, SHARE_COPIED_DELAY)
}

const shareContactFromMenu = async () => {
	$contactActionsDropdown.value?.close()
	await onCopyClientRoute()
}

const openContactInfoFromMenu = () => {
	$contactActionsDropdown.value?.close()
	$clientInfoModal.value?.onOpen(props.client.Id)
}

const openContactEditFromMenu = async () => {
	$contactActionsDropdown.value?.close()
	await nextTick()
	$createEditContactModal.value?.open()
}

const openContactDeleteFromMenu = async () => {
	$contactActionsDropdown.value?.close()
	await nextTick()
	$deleteContactDropdown.value?.open()
}

onBeforeUnmount(() => {
	if (shareTooltipTimer) {
		clearTimeout(shareTooltipTimer)
	}
})

const onCopyPhoneAndEmail = (contact: string) => {
	copyTextToClipboard(contact
		.replace(/<span class='searchedText'>/g, '')
		.replace(/<\/span>/g, '')
	)
}

const onEmail = (email: string) => {
	const link = document.createElement('a');
	const clearEmail = email
		.replace(/<span class='searchedText'>/g, '')
		.replace(/<\/span>/g, '')
	link.href = `mailto:${clearEmail}`;
	link.style.display = 'none';
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
}

const openExternalLink = (url: string) => {
	const clearUrl = url
		.replace(/<span class='searchedText'>/g, '')
		.replace(/<\/span>/g, '')
	const normalizedUrl = normalizeUrlForOpening(clearUrl);

	try {
		new URL(normalizedUrl);
	} catch (error) {
		console.error('Invalid URL:', normalizedUrl);
		return;
	}

	const link = document.createElement('a');
	link.href = normalizedUrl;
	link.target = '_blank';
	link.rel = 'noopener noreferrer';
	link.setAttribute('data-original-url', url);
	link.style.display = 'none';
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
}

const closeAddClientDropdown = () => {
	$addClientToContact.value?.close()
}

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(ContactInfoModal, {
      ref_key: "$clientInfoModal",
      ref: $clientInfoModal,
      title: _ctx.$t('contact_info_title')
    }, null, 8, ["title"]),
    _createElementVNode("div", {
      class: _normalizeClass(['client-item', { 'client-item--touch': _unref(isMobile) || _unref(isTablet) }]),
      "data-contact-id": props.client.Id
    }, [
      (isEditedClient.value)
        ? (_openBlock(), _createBlock(AppLoader, {
            key: 0,
            style: _normalizeStyle(loaderStyle.value)
          }, null, 8, ["style"]))
        : _createCommentVNode("", true),
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("div", _hoisted_3, [
          (_unref(ctx).context === 'project')
            ? (_openBlock(), _createBlock(AppCheckbox, {
                key: 0,
                class: "client-item__header-checkbox",
                value: props.client.MainInProject,
                onOnInput: onMainInProjectChange
              }, null, 8, ["value"]))
            : _createCommentVNode("", true),
          _createElementVNode("div", {
            class: "client-item__header-name",
            innerHTML: highlightedName.value
          }, null, 8, _hoisted_4)
        ]),
        _createElementVNode("div", _hoisted_5, _toDisplayString(clientAddress.value), 1)
      ]),
      _createElementVNode("div", _hoisted_6, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(postSegments.value, (el) => {
          return (_openBlock(), _createElementBlock(_Fragment, {
            key: el.text
          }, [
            (el.isUrl)
              ? (_openBlock(), _createElementBlock("div", {
                  key: 0,
                  class: "client-item__subheader-link",
                  innerHTML: el.text,
                  onClick: ($event: any) => (openExternalLink(el.text))
                }, null, 8, _hoisted_7))
              : (_openBlock(), _createElementBlock("div", {
                  key: 1,
                  class: "client-item__subheader-text",
                  innerHTML: el.text
                }, null, 8, _hoisted_8))
          ], 64))
        }), 128))
      ]),
      _createElementVNode("div", _hoisted_9, [
        (!_unref(isContactsReadonly))
          ? (_openBlock(), _createBlock(AppResizableDropdown, {
              key: 0,
              position: "left",
              resizable: "",
              "initial-width": 400,
              "initial-height": 450,
              "min-width": 400,
              "max-width": 800,
              "min-height": 450,
              "max-height": 900,
              "mobile-title": _ctx.$t('choose_clients'),
              ref_key: "$addClientToContact",
              ref: $addClientToContact,
              name: "$addClientToContact"
            }, {
              activator: _withCtx(() => [
                _createVNode(AppTooltip, {
                  text: _ctx.$t('contact_add_client')
                }, {
                  activator: _withCtx(() => [
                    _createVNode(AppIcon, {
                      class: "client-item__organizations-add-btn",
                      name: "plus",
                      width: "14px",
                      color: "#979797",
                      "hover-color": "#499259",
                      cursor: "pointer"
                    })
                  ]),
                  _: 1
                }, 8, ["text"])
              ]),
              default: _withCtx(() => [
                _createVNode(ContactAddClientDropdown, {
                  "contact-id": props.client.Id,
                  client: props.client,
                  "added-clients": props.client.Clients,
                  "show-close-icon": "",
                  onClose: closeAddClientDropdown
                }, null, 8, ["contact-id", "client", "added-clients"])
              ]),
              _: 1
            }, 8, ["mobile-title"]))
          : _createCommentVNode("", true),
        (!props.client.Clients.length && !_unref(isContactsReadonly))
          ? (_openBlock(), _createElementBlock("span", _hoisted_10, _toDisplayString(_ctx.$t('contact_add_client')), 1))
          : _createCommentVNode("", true),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(props.client.Clients, (client) => {
          return (_openBlock(), _createElementBlock(_Fragment, {
            key: client.Id
          }, [
            _createElementVNode("span", _hoisted_11, [
              _createVNode(_component_router_link, {
                class: "client-item__organizations-list-item-link",
                to: { query: { page: 'client', id: client.Id } }
              }, {
                default: _withCtx(() => [
                  _createElementVNode("span", {
                    innerHTML: _unref(highlightSearchText)(props.searchText, client.Name)
                  }, null, 8, _hoisted_12)
                ]),
                _: 2
              }, 1032, ["to"]),
              (props.client.Clients.length > 1 && !_unref(isContactsReadonly))
                ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                    _cache[4] || (_cache[4] = _createTextVNode(" ")),
                    _createVNode(AppResizableDropdown, {
                      ref_for: true,
                      ref: el => setDetachDropdown(client.Id, el),
                      position: "left",
                      "centered-on-mobile": "",
                      "close-by-click-outside": true
                    }, {
                      activator: _withCtx(() => [
                        _createVNode(AppTooltip, {
                          text: _ctx.$t('contact_detach')
                        }, {
                          activator: _withCtx(() => [
                            _createVNode(AppIcon, {
                              class: "client-item__organizations-list-item-delete",
                              name: "close-bold",
                              width: "12px",
                              color: "#D2D2D2",
                              "hover-color": "#BA4C4C",
                              cursor: "pointer"
                            })
                          ]),
                          _: 1
                        }, 8, ["text"])
                      ]),
                      default: _withCtx(() => [
                        _createVNode(ConfirmActionDropdown, {
                          description: _ctx.$t('contact_detach_question'),
                          onCancel: ($event: any) => (closeDetachDropdown(client.Id)),
                          onConfirm: ($event: any) => (onConfirmDetachClient(client.Id))
                        }, null, 8, ["description", "onCancel", "onConfirm"])
                      ]),
                      _: 2
                    }, 1536)
                  ], 64))
                : _createCommentVNode("", true)
            ]),
            _cache[5] || (_cache[5] = _createTextVNode("  "))
          ], 64))
        }), 128))
      ]),
      _createElementVNode("div", _hoisted_13, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(contactsSegments.value, (segments, index) => {
          return (_openBlock(), _createElementBlock("div", {
            class: "client-item__clients-item",
            key: index
          }, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(segments, (el) => {
              return (_openBlock(), _createElementBlock(_Fragment, { key: el }, [
                (el.isPhone)
                  ? (_openBlock(), _createBlock(AppResizableDropdown, {
                      key: 0,
                      position: "bottom",
                      trigger: "hover",
                      "initial-width": 125,
                      "initial-height": 62,
                      "fullscreen-on-mobile": false,
                      "mobile-title": _ctx.$t('contact_choose_action')
                    }, {
                      activator: _withCtx(() => [
                        _createElementVNode("div", _hoisted_14, [
                          _createElementVNode("div", {
                            class: "client-item__clients-item-phone",
                            innerHTML: el.text,
                            onClick: _withModifiers(($event: any) => (callPhone(el.text)), ["stop"])
                          }, null, 8, _hoisted_15),
                          _createVNode(AppIcon, {
                            class: "client-item__clients-item-phone-icon client-item__clients-item-phone-icon--phone",
                            name: "phone"
                          }),
                          _createVNode(AppIcon, {
                            class: "client-item__clients-item-phone-icon client-item__clients-item-phone-icon--called-phone",
                            name: "phone-called"
                          })
                        ])
                      ]),
                      default: _withCtx(() => [
                        _createVNode(ContactsEmailPhoneOptionsDropdown, {
                          type: "phone",
                          onOnCall: ($event: any) => (callPhone(el.text)),
                          onOnCopy: ($event: any) => (onCopyPhoneAndEmail(el.text))
                        }, null, 8, ["onOnCall", "onOnCopy"])
                      ]),
                      _: 2
                    }, 1032, ["mobile-title"]))
                  : (el.isEmail)
                    ? (_openBlock(), _createBlock(AppResizableDropdown, {
                        key: 1,
                        position: "bottom",
                        trigger: "hover",
                        "initial-width": 125,
                        "initial-height": 62,
                        "fullscreen-on-mobile": false,
                        "mobile-title": _ctx.$t('contact_choose_action')
                      }, {
                        activator: _withCtx(() => [
                          _createElementVNode("div", _hoisted_16, [
                            _createElementVNode("a", {
                              class: "client-item__clients-item-email",
                              innerHTML: el.text,
                              onClick: _withModifiers(($event: any) => (onEmail(el.text)), ["stop","prevent"])
                            }, null, 8, _hoisted_17),
                            _createVNode(AppIcon, {
                              class: "client-item__clients-item-email-icon",
                              name: "envelope",
                              color: "#979797"
                            })
                          ])
                        ]),
                        default: _withCtx(() => [
                          _createVNode(ContactsEmailPhoneOptionsDropdown, {
                            type: "email",
                            onOnEmail: ($event: any) => (onEmail(el.text)),
                            onOnCopy: ($event: any) => (onCopyPhoneAndEmail(el.text))
                          }, null, 8, ["onOnEmail", "onOnCopy"])
                        ]),
                        _: 2
                      }, 1032, ["mobile-title"]))
                    : (el.isUrl)
                      ? (_openBlock(), _createElementBlock("div", {
                          key: 2,
                          class: "client-item__clients-item-email",
                          innerHTML: el.text,
                          onClick: _withModifiers(($event: any) => (openExternalLink(el.text)), ["prevent","stop"])
                        }, null, 8, _hoisted_18))
                      : (_openBlock(), _createElementBlock("div", {
                          key: 3,
                          class: "client-item__clients-item-text",
                          innerHTML: _unref(highlightSearchText)(props.searchText, el.text)
                        }, null, 8, _hoisted_19))
              ], 64))
            }), 128))
          ]))
        }), 128))
      ]),
      _createElementVNode("div", _hoisted_20, [
        _createElementVNode("div", _hoisted_21, [
          (!_unref(isContactsReadonly))
            ? (_openBlock(), _createBlock(AppResizableDropdown, {
                key: 0,
                ref_key: "$addTag",
                ref: $addTag,
                position: "left",
                "close-by-click-outside": true,
                "max-width": 640,
                "max-height": 460,
                "mobile-title": _ctx.$t('choose_tags'),
                name: "$addTagContactItem"
              }, {
                activator: _withCtx(() => [
                  _createVNode(AppTooltip, {
                    text: _ctx.$t('file_add_tag')
                  }, {
                    activator: _withCtx(() => [
                      _createVNode(AppIcon, {
                        class: "client-item__footer-tags-tag",
                        name: "hash",
                        width: "16px",
                        color: "#979797",
                        cursor: "pointer",
                        "hover-color": "#3F884F"
                      })
                    ]),
                    _: 1
                  }, 8, ["text"])
                ]),
                default: _withCtx(() => [
                  _createVNode(AppTagSelector, {
                    "model-value": contactTagsModel.value,
                    "entity-type": _unref(TagEntityType).Person,
                    mode: "select",
                    "allow-create": "",
                    "onUpdate:modelValue": onSelectorChange,
                    onCreateTag: onCreateTag,
                    onClose: _cache[0] || (_cache[0] = ($event: any) => ($addTag.value?.close()))
                  }, null, 8, ["model-value", "entity-type"])
                ]),
                _: 1
              }, 8, ["mobile-title"]))
            : (_openBlock(), _createBlock(AppIcon, {
                key: 1,
                class: "client-item__footer-tags-tag",
                name: "hash",
                width: "16px",
                color: "#D2D2D2"
              })),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(props.client.Tags, (tag) => {
            return (_openBlock(), _createElementBlock("span", {
              key: tag.Id,
              class: "client-item__footer-tags-item"
            }, [
              _createElementVNode("span", {
                class: "client-item__footer-tags-item-name",
                innerHTML: _unref(highlightSearchText)(props.searchText, tag.Name.toLowerCase())
              }, null, 8, _hoisted_22),
              (!_unref(isContactsReadonly))
                ? (_openBlock(), _createBlock(AppTooltip, {
                    key: 0,
                    text: _ctx.$t('ui_tag_detach')
                  }, {
                    activator: _withCtx(() => [
                      _createVNode(AppIcon, {
                        class: "client-item__footer-tags-item-delete",
                        name: "close-bold",
                        width: "12px",
                        color: "#D2D2D2",
                        "hover-color": "#BA4C4C",
                        cursor: "pointer",
                        onClick: _withModifiers(($event: any) => (onDetachTag(tag.Id)), ["stop"])
                      }, null, 8, ["onClick"])
                    ]),
                    _: 2
                  }, 1032, ["text"]))
                : _createCommentVNode("", true)
            ]))
          }), 128))
        ]),
        _createElementVNode("div", _hoisted_23, [
          (!_unref(isMobile) && hasAttachedClients.value)
            ? (_openBlock(), _createBlock(AppTooltip, {
                key: 0,
                ref_key: "$shareTooltip",
                ref: $shareTooltip,
                text: shareTooltipText.value
              }, {
                activator: _withCtx(() => [
                  _createVNode(AppIcon, {
                    class: "client-item__footer-options-btn",
                    style: { marginTop: '1px' },
                    name: "share",
                    width: "18px",
                    color: "#979797",
                    "hover-color": "#499259",
                    cursor: "pointer",
                    onClick: onCopyClientRoute
                  })
                ]),
                _: 1
              }, 8, ["text"]))
            : _createCommentVNode("", true),
          (!_unref(isMobile))
            ? (_openBlock(), _createBlock(AppTooltip, {
                key: 1,
                text: _ctx.$t('information')
              }, {
                activator: _withCtx(() => [
                  _createVNode(AppIcon, {
                    class: "client-item__footer-options-btn",
                    name: "info-circle-outline",
                    width: "18px",
                    color: "#979797",
                    "hover-color": "#499259",
                    cursor: "pointer",
                    onClick: _cache[1] || (_cache[1] = ($event: any) => ($clientInfoModal.value.onOpen(props.client.Id)))
                  })
                ]),
                _: 1
              }, 8, ["text"]))
            : _createCommentVNode("", true),
          (!_unref(isContactsReadonly))
            ? (_openBlock(), _createBlock(AppResizableDropdown, {
                key: 2,
                position: "left",
                resizable: "",
                "initial-width": 320,
                "initial-height": 398,
                "min-width": 320,
                "max-width": 640,
                "min-height": 398,
                "max-height": 800,
                "close-by-click-outside": false,
                "hide-by-parent-scroll": false,
                "mobile-title": _ctx.$t('contact_edit_title'),
                ref_key: "$createEditContactModal",
                ref: $createEditContactModal,
                name: "$createEditContactModal"
              }, {
                activator: _withCtx(() => [
                  (!_unref(isMobile))
                    ? (_openBlock(), _createBlock(AppTooltip, {
                        key: 0,
                        text: _ctx.$t('edit_label')
                      }, {
                        activator: _withCtx(() => [
                          _createVNode(AppIcon, {
                            class: "client-item__footer-options-btn",
                            name: "edit-square-outline",
                            width: "18px",
                            color: "#979797",
                            "hover-color": "#499259",
                            cursor: "pointer"
                          })
                        ]),
                        _: 1
                      }, 8, ["text"]))
                    : _createCommentVNode("", true)
                ]),
                default: _withCtx(() => [
                  _createVNode(CreateEditContactDropdown, {
                    client: props.client,
                    type: "edit",
                    onOnClose: _cache[2] || (_cache[2] = ($event: any) => ($createEditContactModal.value.close()))
                  }, null, 8, ["client"])
                ]),
                _: 1
              }, 8, ["mobile-title"]))
            : _createCommentVNode("", true),
          (!_unref(isContactsReadonly))
            ? (_openBlock(), _createBlock(AppResizableDropdown, {
                key: 3,
                position: "left",
                "centered-on-mobile": "",
                "close-by-click-outside": true,
                ref_key: "$deleteContactDropdown",
                ref: $deleteContactDropdown,
                name: "$deleteContactDropdown"
              }, {
                activator: _withCtx(() => [
                  (!_unref(isMobile))
                    ? (_openBlock(), _createBlock(AppTooltip, {
                        key: 0,
                        text: _ctx.$t('remove')
                      }, {
                        activator: _withCtx(() => [
                          _createVNode(AppIcon, {
                            class: "client-item__footer-options-btn",
                            name: "delete-outline",
                            width: "18px",
                            color: "#979797",
                            "hover-color": "#BA4C4C",
                            cursor: "pointer"
                          })
                        ]),
                        _: 1
                      }, 8, ["text"]))
                    : _createCommentVNode("", true)
                ]),
                default: _withCtx(() => [
                  _createVNode(ConfirmActionDropdown, {
                    description: _ctx.$t('contact_delete_question', { name: props.client.Name }),
                    onCancel: _cache[3] || (_cache[3] = ($event: any) => ($deleteContactDropdown.value?.close())),
                    onConfirm: onConfirmDelete
                  }, null, 8, ["description"])
                ]),
                _: 1
              }, 512))
            : _createCommentVNode("", true),
          (_unref(isMobile))
            ? (_openBlock(), _createBlock(AppResizableDropdown, {
                key: 4,
                ref_key: "$contactActionsDropdown",
                ref: $contactActionsDropdown,
                position: "right",
                "mobile-title": _ctx.$t('contact_choose_action')
              }, {
                activator: _withCtx(() => [
                  _createVNode(AppIcon, {
                    class: "client-item__footer-options-btn client-item__footer-options-menu",
                    name: "options-menu",
                    width: "18px",
                    color: "#979797",
                    cursor: "pointer"
                  })
                ]),
                default: _withCtx(() => [
                  _createElementVNode("div", _hoisted_24, [
                    (hasAttachedClients.value)
                      ? (_openBlock(), _createElementBlock("div", {
                          key: 0,
                          class: "client-item__actions-menu-item",
                          onClick: shareContactFromMenu
                        }, [
                          _createVNode(AppIcon, {
                            name: "share",
                            width: "18px",
                            color: "#979797"
                          }),
                          _createElementVNode("span", null, _toDisplayString(_ctx.$t('ui_share')), 1)
                        ]))
                      : _createCommentVNode("", true),
                    _createElementVNode("div", {
                      class: "client-item__actions-menu-item",
                      onClick: openContactInfoFromMenu
                    }, [
                      _createVNode(AppIcon, {
                        name: "info-circle-outline",
                        width: "18px",
                        color: "#979797"
                      }),
                      _createElementVNode("span", null, _toDisplayString(_ctx.$t('information')), 1)
                    ]),
                    (!_unref(isContactsReadonly))
                      ? (_openBlock(), _createElementBlock("div", {
                          key: 1,
                          class: "client-item__actions-menu-item",
                          onClick: openContactEditFromMenu
                        }, [
                          _createVNode(AppIcon, {
                            name: "edit-square-outline",
                            width: "18px",
                            color: "#979797"
                          }),
                          _createElementVNode("span", null, _toDisplayString(_ctx.$t('edit_label')), 1)
                        ]))
                      : _createCommentVNode("", true),
                    (!_unref(isContactsReadonly))
                      ? (_openBlock(), _createElementBlock("div", {
                          key: 2,
                          class: "client-item__actions-menu-item",
                          onClick: openContactDeleteFromMenu
                        }, [
                          _createVNode(AppIcon, {
                            name: "delete-outline",
                            width: "18px",
                            color: "#979797"
                          }),
                          _createElementVNode("span", null, _toDisplayString(_ctx.$t('remove')), 1)
                        ]))
                      : _createCommentVNode("", true)
                  ])
                ]),
                _: 1
              }, 8, ["mobile-title"]))
            : _createCommentVNode("", true)
        ])
      ])
    ], 10, _hoisted_1)
  ], 64))
}
}

})